import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import useAdmin from '@/hooks/useAdmin';
import { useNavigate, Link } from 'react-router-dom';
import ErrorDisplay from '@/components/auth/ErrorDisplay';
import LoginForm from '@/components/auth/LoginForm';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginAdmin, loading: adminLoading, isAdmin } = useAdmin();
  const navigate = useNavigate();
  
  const isMounted = useRef(true);
  const timeoutRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (isMounted.current) {
      setEmail('pradeeppunarka007@gmail.com');
    }
  }, []);
  
  useEffect(() => {
    if (isAdmin) {
      console.log('Already authenticated as admin, redirecting to dashboard');
      navigate('/administrator/dashboard');
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    isMounted.current = true;
    
    setLoading(false);
    setError('');
    
    return () => {
      isMounted.current = false;
      
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading || adminLoading) return;
    
    setError('');
    setLoading(true);
    
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    
    if (!email || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      timeoutRef.current = window.setTimeout(() => {
        if (isMounted.current) {
          console.log('Admin login timeout reached, resetting loading state');
          setLoading(false);
          setError('Login is taking longer than expected. Please try again.');
        }
      }, 30000);
      
      console.log('Attempting admin login...');
      const result = await loginAdmin(email, password);
      console.log('Login result:', result ? 'Success' : 'Failed');
      
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
    } catch (err: any) {
      console.error('Login error in component:', err);
      
      if (isMounted.current) {
        setError(err.message || 'Failed to login. Please check your credentials.');
        toast.error('Login failed: ' + (err.message || 'Authentication error'));
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        
        if (timeoutRef.current !== null) {
          window.clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    }
  };

  const isLoading = loading || adminLoading;

  return (
    <>
      <Helmet>
        <title>Admin Login | Career Guide Kerala</title>
      </Helmet>

      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-brand-blue">Career Guide Kerala</h1>
          <p className="text-lg text-gray-600">Administrator Portal</p>
        </div>

        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Admin Login</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access the admin panel
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <ErrorDisplay error={error} />
            
            <LoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              isLoading={isLoading}
              onSubmit={handleLogin}
            />
          </CardContent>

          <CardFooter className="flex flex-col space-y-2">
            <p className="text-center text-sm text-gray-500 pt-2">
              <Link to="/" className="text-brand-blue hover:underline">
                Return to Main Website
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </>
  );
};

export default AdminLogin;
